import { useState } from "react";
import { Pressable, StyleSheet, Text, View, useWindowDimensions } from "react-native";
import { useTranslation } from "react-i18next";

import type { Point } from "@heraion/shared";

import {
  PARTIAL_BAR_MIN_HEIGHT,
  barFill,
  barHeight,
  barScaleMax,
  partialBarStyle,
} from "../primitives/MiniTrendBars";
import type { UiTheme } from "../theme";
import type { PointSelection } from "./pointSelection";
import { chartPointA11yLabel } from "./WagmiChartA11yPoints";

// PG-63: the primitive for every bar series you can tap; `MiniTrendBars`
// stays the decorative one, and the two share one bar step. First on Reps at
// load. Same point model as the line (`{x, y, label}`), same spoken name per
// point (`chartPointA11yLabel`). Bars share the card width, zero baseline,
// and the value is printed on every bar. It shows the latest
// BAR_CHART_MAX_BARS points (presentation only: the caller's window stays the
// data), and fewer when the card is too narrow for each to get BAR_MIN_SLOT
// px. The selection is the caller's (`usePointSelection`): the row's header
// shows the selected bar.
// PG-64 fetta 2: the weekly series (Rhythm, Streak, volume by tag) add a tick
// under every bar, the week, in place of the first/last date row, and draw
// the week still running as the in-progress outline MiniTrendBars already
// uses (`partialBarStyle`), its number printed like any other.

const BAR_CHART_MAX_BARS = 8;
const BAR_MIN_SLOT = 44;
// PG-64 fetta 2: a slot is also as wide as its widest printed text, so a
// number or a tick is never cut («4,711» at md is 48px). The texts are mono,
// one character is typography.monoAdvanceEm wide («5,230» at 16px is 48px). SLOT_PAD is the air between two neighbours' numbers; at 5
// a four-digit value still gets eight 44px slots, as before.
/** The testID of a chart not measured yet, drawn invisible. */
export const BAR_CHART_UNMEASURED = "bar-chart-unmeasured";
const SLOT_PAD = 5;
const BAR_MAX_WIDTH = 24;
const MARKER_HEIGHT = 3;
// The tallest bar. The printed value's line above it comes from the theme's
// own `md` size (makeStyles), so track and text always agree.
const PLOT_HEIGHT = 48;
const VALUE_LINE_RATIO = 1.3;

export type BarChartProps = {
  theme: UiTheme;
  /** Oldest first, one bar each. */
  points: ReadonlyArray<Point>;
  /** The number printed on a bar, e.g. "13". */
  valueFormat: (y: number) => string;
  /** The value in each bar's spoken name, e.g. "13 × 40.5 kg". Defaults to
   *  `valueFormat`. */
  readoutFormat?: (y: number) => string;
  selection: PointSelection;
  /** A label under each bar (a week, e.g. "22/9"). Absent: the first and
   *  last bars' `label` under the axis, as on reps at load. */
  tick?: (p: Point) => string;
  /** The last point is still running (the current week): drawn as an
   *  outline with its own floor, never read as a collapse. */
  partialLast?: boolean;
};

export function BarChart({
  theme,
  points,
  valueFormat,
  readoutFormat,
  selection,
  tick,
  partialLast = false,
}: BarChartProps) {
  const { t } = useTranslation();
  const [width, setWidth] = useState(0);
  // A Text renders at size x the system text size: so does its slot.
  const { fontScale } = useWindowDimensions();
  const styles = makeStyles(theme);
  if (points.length === 0) return null;
  const tail = points.slice(-BAR_CHART_MAX_BARS);
  const { monoAdvanceEm } = theme.typography;
  const textSlot = (texts: string[], size: number) =>
    Math.ceil(Math.max(0, ...texts.map((x) => x.length)) * size * fontScale * monoAdvanceEm) +
    SLOT_PAD;
  const slot = Math.max(
    BAR_MIN_SLOT,
    textSlot(tail.map((p) => valueFormat(p.y)), theme.typography.sizes.md),
    tick ? textSlot(tail.map(tick), theme.typography.sizes.sm) : 0,
  );
  // Before the first layout the width is unknown: the cap alone applies, and
  // the chart stays invisible until measured, so no frame shows the bars
  // squeezed (a narrow phone, or a screenshot taken before layout). The
  // testID says so to the visual bench, which waits for it to go
  // (tooling/qa/visual-bench.sh).
  const fit = width > 0 ? Math.max(1, Math.floor(width / slot)) : BAR_CHART_MAX_BARS;
  const from = Math.max(0, points.length - Math.min(fit, BAR_CHART_MAX_BARS));
  const shown = points.slice(from);
  const last = shown.length - 1;
  const readout = readoutFormat ?? valueFormat;
  const max = barScaleMax(shown.map((p) => p.y));
  return (
    <View
      style={[styles.root, width > 0 ? null : styles.unmeasured]}
      testID={width > 0 ? undefined : BAR_CHART_UNMEASURED}
      onLayout={(e) => setWidth(e.nativeEvent.layout.width)}
    >
      <View style={styles.plot}>
        {shown.map((p, j) => {
          const i = from + j;
          const on = i === selection.selected;
          const h = barHeight(p.y, max, PLOT_HEIGHT);
          const partial = partialLast && i === points.length - 1;
          return (
            <Pressable
              key={`${p.x}-${i}`}
              accessibilityRole="button"
              accessibilityLabel={chartPointA11yLabel(t, p.label, readout(p.y))}
              // RNW drops `accessibilityState`; `aria-*` reaches both hosts
              // (IconButton.tsx, same choice).
              aria-selected={on}
              onPress={() => selection.select(i)}
              style={({ pressed }) => [styles.slot, pressed ? styles.pressed : null]}
            >
              <View style={styles.track}>
                <Text style={[styles.value, on ? styles.valueOn : null]} numberOfLines={1}>
                  {valueFormat(p.y)}
                </Text>
                <View
                  style={[
                    styles.bar,
                    partial
                      ? {
                          ...partialBarStyle(theme, on ? barFill(theme, true) : undefined),
                          height: Math.min(PLOT_HEIGHT, Math.max(PARTIAL_BAR_MIN_HEIGHT, h)),
                        }
                      : { height: h, backgroundColor: barFill(theme, on) },
                  ]}
                />
              </View>
              {/* The selection is a mark under the bar too, never colour alone. */}
              <View style={[styles.marker, on ? styles.markerOn : null]} />
              {tick ? (
                <Text style={styles.tick} numberOfLines={1}>
                  {tick(p)}
                </Text>
              ) : null}
            </Pressable>
          );
        })}
      </View>
      {tick ? null : (
        <View style={styles.axis}>
          <Text style={styles.axisLabel}>{shown[0]!.label}</Text>
          {last > 0 ? <Text style={styles.axisLabel}>{shown[last]!.label}</Text> : null}
        </View>
      )}
    </View>
  );
}

function makeStyles(theme: UiTheme) {
  const { palette: p, spacing: s, radius: r, typography: ty } = theme;
  const valueLine = Math.ceil(ty.sizes.md * VALUE_LINE_RATIO);
  const mono = { fontFamily: ty.fonts.mono, fontVariant: ["tabular-nums" as const] };
  return StyleSheet.create({
    root: { gap: s.s1 },
    unmeasured: { opacity: 0 },
    plot: { flexDirection: "row", alignItems: "flex-end" },
    slot: { flex: 1, alignItems: "center", borderRadius: r.r1 },
    pressed: { backgroundColor: p.surface2 },
    track: {
      height: PLOT_HEIGHT + valueLine,
      alignSelf: "stretch",
      alignItems: "center",
      justifyContent: "flex-end",
      borderBottomWidth: 1,
      borderColor: p.borderStrong,
    },
    // Full ink at md on every bar: the printed number is the only way to
    // read the value, so it is never the smallest text on the card.
    value: { ...mono, color: p.ink, fontSize: ty.sizes.md, lineHeight: valueLine },
    valueOn: { fontWeight: "700" },
    bar: { width: "70%", maxWidth: BAR_MAX_WIDTH, borderTopLeftRadius: r.r1, borderTopRightRadius: r.r1 },
    marker: { width: "70%", maxWidth: BAR_MAX_WIDTH, height: MARKER_HEIGHT, marginTop: s.s1 },
    markerOn: { backgroundColor: p.ink },
    axis: { flexDirection: "row", justifyContent: "space-between" },
    axisLabel: { ...mono, color: p.ink3, fontSize: ty.sizes.base },
    // sm, the text floor; the slot widens to fit it (textSlot).
    tick: { ...mono, color: p.ink3, fontSize: ty.sizes.sm, marginTop: s.s1 },
  });
}
